import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CardStatus, ContractCard, ContractStatus, PublicSellContractResponse } from '../player-auction.type';
import { PlayerAuctionService } from '../player-auction.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

const POLL_INTERVAL_MS = 20_000;

interface CardsGroup {
  key: string;

  items: ContractCard[];
  count: number;
}

@Component({
  selector: 'app-contract-public-status-seller',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatTooltipModule,
  ],
  templateUrl: './contract-public-status-seller.html',
  styleUrl: './contract-public-status-seller.scss'
})
export class ContractPublicStatusSeller implements OnInit, OnDestroy {

  private _route = inject(ActivatedRoute);
  displayedColumns = ['player', 'buy_now_price', 'cost', 'quality', 'status', 'expire_time'];
  cards: ContractCard[] = [];
  cardGroups: CardsGroup[] = [];

  loading = true;
  contractLoading = true;
  refreshing = false;
  notFound = false;
  contractId: string = '';
  contract: PublicSellContractResponse | null = null;

  private pollSub?: Subscription;

  @ViewChild('imagePreviewTpl') imagePreviewTpl!: TemplateRef<any>;
  private overlayRef: OverlayRef | null = null;
  previewImageUrl = '';

  constructor(
    private service: PlayerAuctionService,
    private snackBar: MatSnackBar,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
  ) { }

  showImagePreview(_origin: HTMLElement, imageUrl: string): void {
    this.previewImageUrl = imageUrl;
    const positionStrategy = this.overlay
      .position().global().centerHorizontally().centerVertically();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      hasBackdrop: true,
    });

    this.overlayRef.backdropClick().subscribe(() => this.hideImagePreview());
    this.overlayRef.attach(new TemplatePortal(this.imagePreviewTpl, this.viewContainerRef));
  }

  hideImagePreview(): void {
    console.log('ad');
    if (this.overlayRef) {
      console.log('as');

      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  get progressPercent(): number {
    if (!this.contract) return 0;
    const actual = this.contract.target_type === 'sent'
      ? this.contract.estimated_sent_coins
      : this.contract.estimated_received_coins;
    return Math.min(100, Math.round((actual / this.contract.target_coins) * 100));
  }

  getContractStatusClass(status: ContractStatus): string {
    switch (status) {
      case 'Pending': return 'contract-status-pending';
      case 'Transferring': return 'contract-status-transferring';
      case 'Stopped': return 'contract-status-stopped';
      case 'Outsourcing': return 'contract-status-outsourcing';
      case 'Done': return 'contract-status-done';
      case 'Canceled': return 'contract-status-canceled';
      case 'Expired': return 'contract-status-expired';
      case 'Failed': return 'contract-status-failed';
    }
  }

  formatDate(d: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  formatIssueType(type: string): string {
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  ngOnInit(): void {
    this.contractId = this._route.snapshot.paramMap.get('contractId')!;
    this.load();
    this.pollSub = timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS).pipe(
      switchMap(() => this.notFound ? EMPTY : [null]),
    ).subscribe(() => this.load());
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  load(): void {
    // Only show the blocking spinners on the very first load. Later refreshes (poll or
    // manual) update the data in place so the page doesn't jump back to a blank state
    // and lose the user's scroll position / expanded cards.
    const isInitialContractLoad = !this.contract && !this.notFound;
    if (isInitialContractLoad) {
      this.contractLoading = true;
    }

    this.refreshing = true;
    this.service.getPublicSellContract(this.contractId).subscribe({
      next: contract => {
        this.contract = contract;
        this.contractLoading = false;
        this.refreshing = false;
        this.notFound = false;
      },
      error: (err: HttpErrorResponse) => {
        this.contractLoading = false;
        this.refreshing = false;
        if (err.status === 404) {
          this.notFound = true;
          this.contract = null;
        } else {
          this.snackBar.open('Error loading contract details', 'Close', { duration: 5000 });
        }
      }
    });

    const isInitialCardsLoad = this.cards.length === 0;
    if (isInitialCardsLoad) {
      this.loading = true;
    }
    this.service.getPublicContractCards(this.contractId).subscribe({
      next: cards => {
        this.cards = cards;
        this.groupCards(this.cards);
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        if (err.status !== 404) {
          this.snackBar.open('Error loading cards', 'Close', { duration: 5000 });
        }
      }
    });
  }

  groupCards(items: ContractCard[]): void {
    const groupMap = new Map<string, CardsGroup>();

    if (!items || items.length === 0) {
      return;
    }

    items.forEach(item => {
      const key = `${item.player_id}-${item.buy_now_price || 0}-${item.bid_price || 0}-${item.quality}-${item.status}`;

      if (groupMap.has(key)) {
        const group = groupMap.get(key)!;
        group.items.push(item);
        group.count++;
      } else {
        groupMap.set(key, {
          key,
          items: [item],
          count: 1
        });
      }
    });

    this.cardGroups = Array.from(groupMap.values()).sort((a, b) => {
      if (a.items[0].status !== b.items[0].status) {
        return a.items[0].status === 'Listed' ? -1 : 1;
      }
      const aMin = Math.min(...a.items.map(i => new Date(i.expire_time).getTime()));
      const bMin = Math.min(...b.items.map(i => new Date(i.expire_time).getTime()));
      return aMin - bMin;
    });
    return;
  }

  formatTimeRemaining(d: string): string {
    if (!d) return '—';
    const diffMs = new Date(d).getTime() - Date.now();
    if (diffMs <= 0) return '-1';
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  }

  getCardStatusClass(status: CardStatus): string {
    switch (status) {
      case 'Listed': return 'status-listed';
      case 'Sold': return 'status-sold';
      case 'Expired': return 'status-expired';
      case 'QuickSold': return 'status-quick-sold';
      case 'LeftBehind': return 'status-left-behind';
    }
  }

  formatCoins(n: number): string {
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  }

  countByStatus(status: CardStatus): number {
    return this.cards.filter(c => c.status === status).length;
  }
}
